import React, { useState, useRef, useLayoutEffect } from 'react';
import GermanyMap, { Location, WorkshopEvent, WorkshopVariant } from './GermanyMap';
import './RegionalPartnersSection.css';

// Coordinates are in the Ger.svg viewBox space (0 0 591.504 800.504)
const PLACEHOLDER_LOCATIONS: Location[] = [
  {
    id: 'salzkotten',
    name: 'Salzkotten',
    x: 186,
    y: 343,
    events: [
      {
        id: 'salzkotten-produktion',
        variant: 'produktion',
        partnerName: 'KI-Werkstatt (Produktion)',
        description: '',
        descriptionSmall: true,
        formRegion: 'Salzkotten',
        nextDate: 'Juni/Juli 2027',
        price: '990 €',
        priceSuffix: 'netto p.P.',
        feeInfo: '100 Euro Rabatt für Mitgliedsbetriebe des\nheder:LAB und des BVMW.',
        logos: [
          { src: '/assets/hederlab.svg', alt: 'hederlab', url: 'https://hederlab.de/' },
          { src: '/assets/bvmw.svg', alt: 'BVMW', url: 'https://www.bvmw.de/de/kontakt/hans-juergen-altrogge' },
        ],
      },
      {
        id: 'salzkotten-verwaltung',
        variant: 'verwaltung',
        partnerName: 'KI-Werkstatt (Verwaltung)',
        description: '',
        descriptionSmall: true,
        formRegion: 'Salzkotten',
        nextDate: 'Juni/Juli 2027',
        price: '990 €',
        priceSuffix: 'netto p.P.',
        feeInfo: '100 Euro Rabatt für Mitgliedsbetriebe des\nheder:LAB und des BVMW.',
        logos: [
          { src: '/assets/hederlab.svg', alt: 'hederlab', url: 'https://hederlab.de/' },
          { src: '/assets/bvmw.svg', alt: 'BVMW', url: 'https://www.bvmw.de/de/kontakt/hans-juergen-altrogge' },
        ],
      },
    ],
  },
  {
    id: 'guetersloh',
    name: 'Gütersloh',
    x: 162,
    y: 325,
    events: [
      {
        id: 'guetersloh-produktion',
        variant: 'produktion',
        partnerName: 'KI-Werkstatt Gütersloh',
        description: '',
        descriptionSmall: true,
        formRegion: 'Gütersloh',
        nextDate: 'Herbst 2027',
        price: '990 €',
        priceSuffix: 'netto p.P.',
        logos: [
          { src: '/assets/ima.svg', alt: 'IMA Innovationsmanufaktur Gütersloh', url: 'https://www.ima-gt.de/' },
        ],
      },
      {
        id: 'guetersloh-verwaltung',
        variant: 'verwaltung',
        partnerName: 'KI-Werkstatt Gütersloh',
        description: '',
        descriptionSmall: true,
        formRegion: 'Gütersloh',
        nextDate: 'Winter 2027',
        price: '990 €',
        priceSuffix: 'netto p.P.',
        logos: [
          { src: '/assets/ima.svg', alt: 'IMA Innovationsmanufaktur Gütersloh', url: 'https://www.ima-gt.de/' },
        ],
      },
    ],
  },
  {
    id: 'remscheid',
    name: 'Remscheid',
    x: 85,
    y: 390,
    events: [
      {
        id: 'remscheid-produktion',
        variant: 'produktion',
        partnerName: 'KI-Werkstatt Remscheid',
        description: '',
        descriptionSmall: true,
        formRegion: 'Remscheid',
        nextDate: 'Juni/Juli 2027',
        price: '990 €',
        priceSuffix: 'netto p.P.',
        logos: [
          { src: '/assets/bzi.svg', alt: 'BZI Remscheid', url: 'https://www.bzi-rs.de/' },
        ],
      },
      {
        id: 'remscheid-verwaltung',
        variant: 'verwaltung',
        partnerName: 'KI-Werkstatt Remscheid',
        description: '',
        descriptionSmall: true,
        formRegion: 'Remscheid',
        nextDate: 'Juni/Juli 2027',
        price: '990 €',
        priceSuffix: 'netto p.P.',
        logos: [
          { src: '/assets/bzi.svg', alt: 'BZI Remscheid', url: 'https://www.bzi-rs.de/' },
        ],
      },
    ],
  },
];

interface RegionalPartnersSectionProps {
  onRegionSelect?: (region: string) => void;
  /** If set, only events of this variant are shown. If omitted, all events are shown with pagination. */
  variant?: WorkshopVariant;
}

const subtitleForVariant = (v: WorkshopVariant): string =>
  v === 'verwaltung' ? 'Schwerpunkt Verwaltung' : 'Schwerpunkt Produktion';

/** Renders the info body content for a given location+event (used both visibly and for measurement). */
const InfoBody: React.FC<{
  location: Location;
  event: WorkshopEvent;
  onAnmelden: () => void;
  eventCount?: number;
  eventIndex?: number;
  onPrev?: () => void;
  onNext?: () => void;
}> = ({ location, event, onAnmelden, eventCount, eventIndex = 0, onPrev, onNext }) => {
  const showPager = typeof eventCount === 'number' && eventCount > 1;
  return (
    <div className="partner-info">
      <div className="partner-location-bar">
        <span>{location.name}</span>
        {showPager && <span className="partner-event-count">{eventCount}</span>}
      </div>
      {showPager && (
        <>
          <button
            type="button"
            className="partner-event-nav-btn prev"
            onClick={onPrev}
            aria-label="Vorheriger Workshop"
            disabled={eventIndex <= 0}
          >
            ‹
          </button>
          <button
            type="button"
            className="partner-event-nav-btn next"
            onClick={onNext}
            aria-label="Nächster Workshop"
            disabled={eventIndex >= (eventCount ?? 1) - 1}
          >
            ›
          </button>
        </>
      )}
      <div className="partner-info-body">
        <h3 className="partner-name">KI-Werkstatt</h3>
        <p className="partner-subtitle">{subtitleForVariant(event.variant)}</p>
        {event.description && (
          <p className={event.descriptionSmall ? 'partner-price-suffix' : 'partner-description'}>
            {event.description}
          </p>
        )}

        {event.logos && event.logos.length > 0 && (
          <div>
            <p className="partner-section-label">Regionale Partner</p>
            <div className={`partner-logos-grid${event.logos.length === 1 ? ' partner-logos-single' : ''}`}>
              {event.logos.map((logo) => (
                <div key={logo.src} className="partner-logo-cell">
                  {logo.url ? (
                    <a href={logo.url} target="_blank" rel="noopener noreferrer">
                      <img src={logo.src} alt={logo.alt} className="partner-logo-img" />
                    </a>
                  ) : (
                    <img src={logo.src} alt={logo.alt} className="partner-logo-img" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {(event.nextDate || event.price) && (
          <div className="partner-details-grid">
            {event.nextDate && (
              <>
                <span className="partner-detail-label">Nächster Termin</span>
                <span className="partner-description">{event.nextDate}</span>
              </>
            )}
            {event.price && (
              <>
                <span className="partner-detail-label">Teilnahmegebühr</span>
                <span className="partner-price-plain">
                  <strong>{event.price}</strong>
                  {event.priceSuffix && (
                    <span className="partner-price-suffix"> {event.priceSuffix}</span>
                  )}
                </span>
              </>
            )}
          </div>
        )}
        {event.feeInfo && <p className="partner-fee-note">{event.feeInfo}</p>}

        <button className="partner-anmelden-btn" onClick={onAnmelden}>
          Unverbindliche Anmeldung
        </button>
      </div>
    </div>
  );
};

const RegionalPartnersSection: React.FC<RegionalPartnersSectionProps> = ({ onRegionSelect, variant }) => {
  // Filter events by variant (if provided); drop locations with no matching events.
  const visibleLocations: Location[] = React.useMemo(() => {
    if (!variant) return PLACEHOLDER_LOCATIONS;
    return PLACEHOLDER_LOCATIONS
      .map((loc) => ({ ...loc, events: loc.events.filter((e) => e.variant === variant) }))
      .filter((loc) => loc.events.length > 0);
  }, [variant]);

  const [activeLocationId, setActiveLocationId] = useState<string>(
    visibleLocations[0]?.id ?? ''
  );
  // Per-location current event index (only used for overview pagination).
  const [eventIndexByLocation, setEventIndexByLocation] = useState<Record<string, number>>({});
  const [panelHeight, setPanelHeight] = useState<number | undefined>(undefined);
  const measureRef = useRef<HTMLDivElement>(null);

  // After first paint, measure all hidden panels (one per location+event) and use the tallest.
  useLayoutEffect(() => {
    if (!measureRef.current) return;
    const panels = Array.from(measureRef.current.children) as HTMLElement[];
    if (panels.length === 0) return;
    const max = Math.max(...panels.map((p) => p.offsetHeight));
    if (max > 0) setPanelHeight(max);
  }, [variant]);

  const activeLocation = visibleLocations.find((loc) => loc.id === activeLocationId);
  const activeEventIndex = activeLocation ? eventIndexByLocation[activeLocation.id] ?? 0 : 0;
  const activeEvent = activeLocation?.events[activeEventIndex] ?? activeLocation?.events[0];

  const handleAnmelden = (_location: Location, event: WorkshopEvent) => {
    // On a variant-specific page (onRegionSelect provided) → update form + scroll in-page.
    if (onRegionSelect) {
      if (event.formRegion) onRegionSelect(event.formRegion);
      const section = document.getElementById('registration-section');
      if (section) section.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    // On the overview page → navigate to the matching variant page with region as query param.
    const params = new URLSearchParams();
    if (event.formRegion) params.set('region', event.formRegion);
    const query = params.toString();
    window.location.href = `/ki-werkstatt/${event.variant}${query ? `?${query}` : ''}`;
  };

  const cycleEvent = (location: Location, direction: 1 | -1) => {
    setEventIndexByLocation((prev) => {
      const count = location.events.length;
      const current = prev[location.id] ?? 0;
      const next = Math.min(Math.max(current + direction, 0), count - 1);
      return { ...prev, [location.id]: next };
    });
  };

  return (
    <section className="regional-partners-section">
      {/* Hidden measurement container — renders every location+event combo off-screen to find max height */}
      <div
        ref={measureRef}
        style={{
          position: 'fixed',
          left: '-9999px',
          top: 0,
          width: '400px',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {visibleLocations.flatMap((loc) =>
          loc.events.map((ev) => (
            <div key={`${loc.id}-${ev.id}`}>
              <InfoBody
                location={loc}
                event={ev}
                onAnmelden={() => {}}
                eventCount={loc.events.length}
              />
            </div>
          ))
        )}
      </div>

      <div className="container">
        <h2 className="section-title">
          <span className="highlight-text">Standorte</span> unserer KI-Werkstätten
        </h2>
        <p className="section-subtitle">
          Finde einen Workshop-Standort in Deiner Nähe
        </p>

        <div className="regional-partners-grid">
          {/* Left: Map */}
          <div className="map-container">
            <GermanyMap
              locations={visibleLocations}
              activeLocationId={activeLocationId}
              onLocationInteract={setActiveLocationId}
            />
          </div>

          {/* Right: Info Panel */}
          <div
            className="info-panel"
            style={panelHeight ? { height: panelHeight } : undefined}
          >
            {activeLocation && activeEvent && (
              <InfoBody
                location={activeLocation}
                event={activeEvent}
                onAnmelden={() => handleAnmelden(activeLocation, activeEvent)}
                eventCount={activeLocation.events.length}
                eventIndex={activeEventIndex}
                onPrev={() => cycleEvent(activeLocation, -1)}
                onNext={() => cycleEvent(activeLocation, 1)}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegionalPartnersSection;
